/**
 * "Top Performing Booths" grafiğinin kabin sayısına göre ölçeklenme kuralları.
 *
 * NEDEN AYRI DOSYA: bunlar saf hesaplardır ve tarayıcısız test edilebilirler
 * (`node scripts/test-boothChartLayout.mjs`). Bileşenin içinde gömülü
 * kaldıklarında ancak gözle kontrol edilebiliyorlardı.
 *
 * 🔴 2026-08-17 müşteri: "Kabin sayısı artışı olduğunda lokasyon isimlerinin
 * yazdığı grafiği sayı artışına uygun yerleştirmeniz mümkün mü? Belki daha
 * inceltilebilir. Tahmini kabin sayısı 50 adet gibi olur."
 *
 * 50 KABİNLE ÖLÇÜLDÜ (gerçek ApexCharts, headless Chrome):
 *   • Satır yüksekliği sabit 40px idi → grafik 2015px, kaydırma penceresi 400px
 *     → aynı anda grafiğin yalnızca **%19,9**'u görünüyordu (50 kabinde 10 tanesi),
 *     10.'su da alt kenarda ortadan kesiliyordu.
 *   • Varsayılan görünümdeki "Others (45)" çubuğu 45 kabinin TOPLAMI olduğu için
 *     en büyük gerçek çubuğun **5,6 KATI** uzunluğa ulaşıyor, gerçek çubukların
 *     hepsini çizim alanının soluna eziyordu.
 *
 * Yeni değerlerle 50 kabinde görünen oran **%51,9**'a çıkıyor (26 kabin),
 * 6 ve 12 kabinde ise hiç kaydırma gerekmiyor — yani bugünkü filo etkilenmiyor.
 */

/** Kaydırma penceresinin üst sınırı (px). 400 → 620. */
export const CHART_MAX_HEIGHT = 620

/** Eksen + iç boşluk payı; satırların dışında kalan sabit yükseklik. */
export const CHART_AXIS_PADDING = 80

/** Bu kabin sayısının üzerinde "yoğun mod" devreye girer. */
export const DENSE_THRESHOLD = 15

/**
 * Satır yüksekliği kabin sayısıyla incelir.
 *
 * Kademeli, çünkü sürekli bir formül küçük filolarda çubukları gereksiz
 * inceltirdi: 6 kabinlik bugünkü kurulumda görünüm AYNEN korunmalı (40px).
 */
export function boothChartRowHeight(visibleCount: number): number {
  if (visibleCount <= 8) return 40
  if (visibleCount <= 15) return 32
  if (visibleCount <= 30) return 26
  if (visibleCount <= 60) return 22
  return 18
}

export type BoothChartLayout = {
  /** Daraltılmış görünümde gösterilecek kabin sayısı. */
  topN: number
  /** Daraltılmış görünümün dışında kalan kabin sayısı. */
  restCount: number
  /** "Others" toplayıcı çubuğu gösterilsin mi? */
  showOthers: boolean
  /** Grafikte fiilen çizilecek satır sayısı. */
  visibleCount: number
  rowHeight: number
  /** Yoğun mod: ince çubuk, küçük yazı, eksen başlığı yok. */
  dense: boolean
  /** Grafiğin toplam yüksekliği (px). */
  height: number
  /** Kart içinde dikey kaydırma gerekiyor mu? */
  needsScroll: boolean
}

/**
 * @param boothCount toplam kabin sayısı
 * @param showAll    kullanıcı "Show All"a bastı mı
 */
export function boothChartLayout(boothCount: number, showAll: boolean): BoothChartLayout {
  // Filo büyüdükçe "ilk N" de büyür: 50 kabinde ilk 5'i göstermek, filonun
  // %90'ını tek bir kutuya saklamak demekti.
  const topN = boothCount > 12 ? 10 : 5
  const restCount = Math.max(0, boothCount - topN)

  // "Others" yalnızca KÜÇÜK bir kalanı özetlerken dürüsttür. Kalan kalabalıksa
  // toplamı en büyük gerçek çubuğu katbekat aşar ve ölçeği yalan söyletir
  // (50 kabinde ölçüldü: 5,6 kat). O durumda çubuk yerine yazıyla bildiriyoruz.
  const showOthers = restCount > 0 && restCount <= topN

  const visibleCount = showAll
    ? boothCount
    : Math.min(boothCount, topN + (showOthers ? 1 : 0))

  const rowHeight = boothChartRowHeight(visibleCount)
  const height = Math.max(250, visibleCount * rowHeight + CHART_AXIS_PADDING)

  return {
    topN,
    restCount,
    showOthers,
    visibleCount,
    rowHeight,
    dense: visibleCount > DENSE_THRESHOLD,
    height,
    needsScroll: height > CHART_MAX_HEIGHT,
  }
}
